import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, ArrowLeft } from "lucide-react";
import TileSphereLogo from "@/components/TileSphereLogo";
import SubscriptionBuilder from "@/components/SubscriptionBuilder";
import RoleHierarchyExplorer from "@/components/RoleHierarchyExplorer";

export default function PortalPage({ portal, title, tagline, gradient, heroBg, benefits, features, faqs, accentColor }) {
  return (
    <div className="min-h-screen bg-[#0a0a14] text-white" data-testid={`portal-page-${portal.toLowerCase()}`}>
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <TileSphereLogo />
          <div className="flex items-center gap-2">
            <Link to="/"><Button variant="ghost" className="text-white/70 hover:bg-white/10" data-testid="back-home"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>
            <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10">Sign in</Button></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className={`relative px-6 py-28 overflow-hidden bg-gradient-to-br ${gradient}`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.12),transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70 font-bold">{portal.toLowerCase()} portal</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black mt-3 leading-[0.95]">{title}</h1>
            <p className="mt-5 text-white/80 max-w-xl text-lg">{tagline}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#subscription"><Button size="lg" className="bg-white text-black hover:bg-white/90 rounded-xl font-bold" data-testid="hero-cta-builder">Configure subscription <ArrowRight className="w-4 h-4 ml-1" /></Button></a>
              <a href="#demo"><Button size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-xl" data-testid="hero-cta-demo">Talk to sales</Button></a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="lg:col-span-5">
            <div className="relative aspect-square rounded-3xl bg-black/30 backdrop-blur-xl border border-white/15 overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center text-9xl opacity-30">{heroBg}</div>
              <div className="absolute inset-0 bg-[conic-gradient(from_0deg,rgba(255,255,255,0.08),transparent,rgba(255,255,255,0.08))] animate-spin-slow" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Why teams choose this portal</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mt-3">Built for how you actually work.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {benefits.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} viewport={{ once: true }}
                className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur"
                data-testid={`benefit-card-${i}`}
              >
                <div className="font-display text-xl font-bold text-white">{b.title}</div>
                <div className="text-sm text-white/60 mt-2">{b.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Features</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mt-3">Everything you need. Nothing you don&apos;t.</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-10">
            {features.map((f) => (
              <div key={f} className="flex items-start gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`feature-row-${f.replace(/\s+/g, "-").toLowerCase()}`}>
                <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-white/80">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hierarchy explorer for this portal */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Role hierarchy</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mt-3">Map your team in seconds.</h2>
          <div className="mt-10"><RoleHierarchyExplorer /></div>
        </div>
      </section>

      {/* Subscription Builder */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-[#0a0a14]" id="subscription">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Dynamic subscription builder</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mt-3">Compare plans — see exactly what&apos;s included.</h2>
          <p className="mt-3 text-white/60">Compare plans → see exactly what&apos;s included → optional add-ons → request your demo.</p>
          <div className="mt-10"><SubscriptionBuilder portal={portal} accentColor={accentColor} /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Common questions</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black mt-3">Frequently asked.</h2>
          <div className="mt-10 space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="group p-5 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer text-white font-semibold list-none flex items-center justify-between">
                  {f.q}
                  <span className="text-white/40 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-white/70">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Demo CTA */}
      <section className="py-24 px-6" id="demo">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`inline-block p-1 rounded-full bg-gradient-to-r ${accentColor}`}>
            <div className="px-10 py-12 rounded-full bg-[#0a0a14]">
              <h2 className="font-display text-4xl sm:text-5xl font-black">Ready when you are.</h2>
              <p className="mt-3 text-white/60">Get a tailored walkthrough of the {portal.toLowerCase()} portal — 30 minutes, no fluff.</p>
              <a href="#subscription"><Button size="lg" className="mt-7 bg-white text-black hover:bg-white/90 rounded-xl font-bold" data-testid="cta-bottom">Configure & request demo</Button></a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-6 bg-black text-white/50 text-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <TileSphereLogo subtle />
          <div>© 2026 TileSphere · An ecosystem by Projexino</div>
        </div>
      </footer>
    </div>
  );
}
